import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from './components/Footer';

export default function DaringFuturesChallenger() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-roboto">
      {/* Fixed Navigation Header */}
        <nav className="fixed top-0 w-full bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src={process.env.PUBLIC_URL + '/df-web-logo.svg'} 
                alt="Daring Futures - Realising Ambition" 
                className="h-16" 
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a 
                href="#truth" 
                className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] transition-colors"
              >
                The Truth
              </a>
              <a 
                href="#framework" 
                className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] transition-colors"
              >
                Our Approach
              </a>
              <a 
                href="#who" 
                className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] transition-colors"
              >
                Who We Work With
              </a>
              <a 
                href="https://calendly.com/andrewkidd/truth-session" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-[#86C2CA] hover:bg-[#3B4F6B] text-white font-poppins font-bold px-6 py-2.5 rounded transition-all shadow-sm"
              >
                Book Truth Session
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#062B49]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-2 border-t border-gray-200 mt-4">
              <div className="flex flex-col gap-3">
                <a href="#truth" className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] py-2">
                  The Truth
                </a>
                <a href="#framework" className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] py-2">
                  Our Approach
                </a>
                <a href="#who" className="font-poppins text-sm text-[#062B49] hover:text-[#86C2CA] py-2">
                  Who We Work With
                </a>
                <a 
                  href="https://calendly.com/andrewkidd/truth-session" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#86C2CA] hover:bg-[#3B4F6B] text-white font-poppins font-bold px-6 py-2.5 rounded transition-all text-center"
                >
                  Book Truth Session
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - The Pattern Interrupt */}
      <section className="pt-32 pb-20 px-6 bg-[#062B49] text-white min-h-[85vh] flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Bold Statement */}
            <div>
              <h1 className="font-poppins font-bold text-5xl md:text-6xl lg:text-7xl leading-tight mb-6 text-white">
                Your transformation dashboards are lying to you.
              </h1>
              <p className="text-xl md:text-2xl leading-relaxed text-gray-300 mb-8">
                The real cost isn't in your roadmap.<br/>
                It's in the hidden work draining your teams, your budget, and your belief that change is even possible.
              </p>
              <a 
                href="#diagnostic" 
                className="inline-block bg-[#86C2CA] hover:bg-white hover:text-[#062B49] text-white font-poppins font-bold px-8 py-4 rounded transition-all shadow-lg text-lg"
              >
                Take the 5-Minute Diagnostic →
              </a>
            </div>

            {/* Right: The Cost */}
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-lg border border-white/20">
              <div className="space-y-6">
                <div className="text-center border-b border-white/20 pb-6">
                    <div className="font-mono text-6xl font-bold text-[#86C2CA] mb-2">£2–4M</div>
                    <div className="text-sm text-gray-300">The hidden work tax on every £10m transformation—<br/>lost to invisible costs your dashboards never track</div>
                </div>
                <div className="text-center border-b border-white/20 pb-6">
                    <div className="font-mono text-6xl font-bold text-[#86C2CA] mb-2">43%</div>
                    <div className="text-sm text-gray-300">Of transformation value is lost before you even<br/>start executing (McKinsey, 2024)</div>
                </div>
                <div className="text-center">
                    <div className="font-mono text-6xl font-bold text-[#86C2CA] mb-2">18-months</div>
                    <div className="text-sm text-gray-300">To realize 58–74% of your value—<br/>and that's if you can see what's really slowing you down</div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-6 text-center italic">
                Based on McKinsey transformation research and our Hidden Work diagnostic data.<br/>
                Your transformation is bleeding value while everyone stares at green RAG statuses.
            </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Lie Section */}
      <section id="truth" className="py-20 px-6 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-poppins font-bold text-4xl md:text-5xl text-[#062B49] mb-4">
              The Truth Others Won't Tell You
            </h2>
            <p className="text-xl text-[#697A90] max-w-3xl mx-auto">
              Most transformations fail not because of bad strategy or lazy teams.<br/>
              They fail because of <span className="font-bold text-[#062B49]">hidden work</span>.
            </p>
          </div>

          {/* Split Truth Section */}
          <div className="grid md:grid-cols-2 gap-0 rounded-lg overflow-hidden shadow-2xl">
            {/* Left: What They Tell You */}
            <div className="bg-[#152C47] text-white p-12">
              <h3 className="font-poppins font-bold text-2xl mb-8 text-[#86C2CA]">
                What They Tell You
              </h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-3">
                  <span className="text-[#86C2CA] text-xl">→</span>
                  <span className="text-lg">"Your transformation is on track"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#86C2CA] text-xl">→</span>
                  <span className="text-lg">"Teams are aligned and engaged"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#86C2CA] text-xl">→</span>
                  <span className="text-lg">"Metrics show steady progress"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#86C2CA] text-xl">→</span>
                  <span className="text-lg">"We just need better execution"</span>
                </li>
              </ul>
            </div>

            {/* Right: What's Really Happening */}
            <div className="bg-[#EAEFF5] text-[#062B49] p-12">
              <h3 className="font-poppins font-bold text-2xl mb-8">
                What's Really Happening
              </h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-3">
                  <span className="text-[#062B49] text-xl font-bold">→</span>
                  <span className="text-lg">Decision cycles expand as accountability shrinks</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#062B49] text-xl font-bold">→</span>
                  <span className="text-lg">Surface agreement masks political fractures</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#062B49] text-xl font-bold">→</span>
                  <span className="text-lg">Hidden work compounds invisibly</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#062B49] text-xl font-bold">→</span>
                  <span className="text-lg">Your best people are quietly burning out</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-2xl font-poppins font-bold text-[#062B49]">
              The difference? Hidden work.
            </p>
            <p className="text-xl text-[#697A90] mt-2">
              And we've built the only system that surfaces it.
            </p>
          </div>
        </div>
      </section>

      {/* 3am Section - You're Not Imagining It */}
      <section className="py-20 px-6 bg-[#062B49] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-poppins font-bold text-4xl md:text-5xl mb-4 text-white">
              You're not imagining it.
            </h2>
            <p className="text-xl text-gray-300">
              At 3 a.m., these thoughts keep transformation leaders awake.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Mark - Transformation Leader */}
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10">
              <div className="text-[#86C2CA] font-poppins font-bold text-sm mb-4 uppercase tracking-wide">
                Transformation Leader
              </div>
              <h3 className="font-poppins font-bold text-2xl mb-6 text-white">Mark's 3 a.m. Thought:</h3>
              <div className="bg-[#152C47] p-6 rounded-lg mb-6 border-l-4 border-[#86C2CA]">
                <p className="text-xl italic">"Why am I the bottleneck again?"</p>
              </div>
              <div className="space-y-4 text-gray-300">
                <div>
                  <p className="font-bold text-white mb-2">What it really means:</p>
                  <p>System dependence from unclear decision-rights</p>
                </div>
                <div>
                  <p className="font-bold text-white mb-2">The hidden cost:</p>
                  <p>Governance gaps creating centralised uncertainty—every decision flows through Mark, creating delays and exhaustion</p>
                </div>
              </div>
            </div>

            {/* Sarah - Senior Leader */}
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10">
              <div className="text-[#86C2CA] font-poppins font-bold text-sm mb-4 uppercase tracking-wide">
                Senior Leader
              </div>
              <h3 className="font-poppins font-bold text-2xl mb-6 text-white">Sarah's 3 a.m. Thought:</h3>
              <div className="bg-[#152C47] p-6 rounded-lg mb-6 border-l-4 border-[#86C2CA]">
                <p className="text-xl italic">"Am I doing enough?"</p>
              </div>
              <div className="space-y-4 text-gray-300">
                <div>
                  <p className="font-bold text-white mb-2">What it really means:</p>
                  <p>Invisible emotional labour—holding everyone together</p>
                </div>
                <div>
                  <p className="font-bold text-white mb-2">The hidden cost:</p>
                  <p>Compassion fatigue and identity erosion—constantly smoothing conflicts while wondering if anyone sees the weight she's carrying</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-xl text-gray-300 mb-6">
              <span className="font-bold text-white">You're not alone.</span> We've mapped exactly how hidden work shows up—and how to resolve it.
            </p>
          </div>
        </div>
      </section>

      {/* The Challenger Manifesto */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-poppins font-bold text-4xl md:text-5xl text-[#062B49] mb-8 text-center">
            We don't smooth over reality.<br/>
            We surface it.
          </h2>
          
          <div className="space-y-6 text-lg text-[#697A90] leading-relaxed mb-12">
            <p>
              While others obsess over delivery mechanics, we measure the <span className="font-bold text-[#062B49]">human friction</span> that silently distorts them.
            </p>
            <p>
              We expose what's unspoken, untracked, and unmeasured—the conversations, assumptions, and avoidance patterns that drag every decision downstream.
            </p>
            <p className="text-2xl font-poppins font-bold text-[#062B49] text-center py-6">
              We're not here to reassure you.<br/>
              We're here to wake you up.
            </p>
            <p>
              Because transformation isn't failing for lack of skill—it's failing for lack of truth.
            </p>
          </div>

          {/* Three Principles */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">1</span>
              </div>
              <p className="font-poppins font-bold text-[#062B49] mb-2">Detect & Quantify</p>
              <p className="text-sm text-[#697A90]">Hidden work as lost margin, wasted energy, and reputational risk</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">2</span>
              </div>
              <p className="font-poppins font-bold text-[#062B49] mb-2">Re-Pattern Systems</p>
              <p className="text-sm text-[#697A90]">The structures, relationships, and dynamics that create hidden work</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">3</span>
              </div>
              <p className="font-poppins font-bold text-[#062B49] mb-2">Build Capability</p>
              <p className="text-sm text-[#697A90]">Leaders who can see the invisible before it compounds</p>
            </div>
          </div>

          <div className="bg-[#EAEFF5] p-8 rounded-lg text-center">
            <p className="text-xl font-poppins text-[#062B49] mb-2">
              This is not management consulting.
            </p>
            <p className="text-2xl font-poppins font-bold text-[#062B49]">
              This is operational truth-telling.
            </p>
          </div>
        </div>
      </section>

      {/* The Framework Section */}
      <section id="framework" className="py-20 px-6 bg-[#EAEFF5] scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-poppins font-bold text-4xl md:text-5xl text-[#062B49] mb-4">
              The Uncertainty Management Framework™
            </h2>
            <p className="text-xl text-[#697A90] max-w-3xl mx-auto">
              The only framework that measures hidden work across three critical domains
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Deciding */}
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-[#062B49] rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-2xl text-[#062B49] mb-4">
                DECIDING
              </h3>
              <p className="text-[#697A90] mb-4">What do we do next?</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clear:</span> Strategic direction is defined</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clouded:</span> Uncertainty slowing choices</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Compounded:</span> Decision paralysis</span>
                </div>
              </div>
            </div>

            {/* Executing */}
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-[#062B49] rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-2xl text-[#062B49] mb-4">
                EXECUTING
              </h3>
              <p className="text-[#697A90] mb-4">How do we do it well?</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clear:</span> Delivery is predictable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clouded:</span> Execution drag emerging</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Compounded:</span> Chronic delivery failure</span>
                </div>
              </div>
            </div>

            {/* Relating */}
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-16 h-16 bg-[#062B49] rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-2xl text-[#062B49] mb-4">
                RELATING
              </h3>
              <p className="text-[#697A90] mb-4">How do we work together?</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clear:</span> Trust and alignment present</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Clouded:</span> Relational friction building</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-[#697A90]"><span className="font-bold">Compounded:</span> Psychological safety broken</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg text-center shadow-sm">
            <p className="text-xl text-[#697A90] mb-2">
              Used to diagnose <span className="font-mono font-bold text-[#062B49]">£47M</span> in hidden transformation costs
            </p>
            <p className="text-sm text-[#697A90]">
              across software companies from 50-5000 employees
            </p>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section id="who" className="py-20 px-6 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-poppins font-bold text-4xl md:text-5xl text-[#062B49] text-center mb-16">
            Who we work with
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Transformation Leaders */}
            <div className="bg-[#EAEFF5] p-8 rounded-lg">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#062B49] mb-4">
                Transformation leaders drowning in system dependence
              </h3>
              <p className="text-[#697A90] text-sm leading-relaxed">
                You're the one who sees the hidden work piling up—the governance gaps, the political misalignment, the team burning out—while everyone else focuses on delivery dates. We help you make the invisible visible and redistribute the load you're carrying alone.
              </p>
            </div>

            {/* Senior Leaders */}
            <div className="bg-[#EAEFF5] p-8 rounded-lg">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#062B49] mb-4">
                Senior leaders carrying invisible emotional labour
              </h3>
              <p className="text-[#697A90] text-sm leading-relaxed">
                You're smoothing over conflicts, holding everyone together, integrating cultures not just processes—and you're exhausted. We help you name the relational work no one else sees and create boundaries that protect your energy without letting people down.
              </p>
            </div>

            {/* Coaches */}
            <div className="bg-[#EAEFF5] p-8 rounded-lg">
              <div className="w-16 h-16 bg-[#86C2CA] rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#062B49] mb-4">
                Agile coaches stuck in surface-level fixes
              </h3>
              <p className="text-[#697A90] text-sm leading-relaxed">
                Your clients want transformation, but you're navigating hidden politics, unspoken tensions, and dependency dynamics the org chart doesn't show. We help you develop the psychological and systemic literacy to go deeper.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Truth Session CTA */}
        <section id="diagnostic" className="py-20 px-6 bg-[#062B49] text-white scroll-mt-24">
        <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
            <h2 className="font-poppins font-bold text-4xl md:text-5xl mb-6 text-white">
                The Truth Session
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                90 minutes. £750. No sales pitch.
            </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-10 rounded-lg border border-white/20 mb-8">
            <h3 className="font-poppins font-bold text-2xl mb-6 text-[#86C2CA]">What happens:</h3>
            <ol className="space-y-4 mb-8">
                <li className="flex items-start gap-4">
                <span className="font-mono font-bold text-[#86C2CA] text-xl">01</span>
                <span className="text-lg">We run your leadership through the Hidden Work framework</span>
                </li>
                <li className="flex items-start gap-4">
                <span className="font-mono font-bold text-[#86C2CA] text-xl">02</span>
                <span className="text-lg">You see where your transformation is bleeding money and morale</span>
                </li>
                <li className="flex items-start gap-4">
                <span className="font-mono font-bold text-[#86C2CA] text-xl">03</span>
                <span className="text-lg">We map the financial cost in real numbers</span>
                </li>
                <li className="flex items-start gap-4">
                <span className="font-mono font-bold text-[#86C2CA] text-xl">04</span>
                <span className="text-lg">You get the truth your dashboards won't tell you</span>
                </li>
            </ol>

            <div className="bg-[#152C47] p-6 rounded-lg mb-6 border-l-4 border-[#86C2CA]">
                <p className="text-lg mb-4">
                This isn't a free diagnostic designed to sell you something.<br/>
                It's a paid session that shows you what's really happening.
                </p>
                <p className="text-lg">
                What you choose to do with that truth is up to you.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 pt-6 border-t border-white/20">
                <div>
                <p className="text-sm text-gray-400 mb-1">Duration</p>
                <p className="font-poppins font-bold text-xl">90 minutes</p>
                </div>
                <div>
                <p className="text-sm text-gray-400 mb-1">Investment</p>
                <p className="font-poppins font-bold text-xl">£750</p>
                <p className="text-xs text-gray-400">(fully credited if you engage)</p>
                </div>
                <div>
                <p className="text-sm text-gray-400 mb-1">Outcome</p>
                <p className="font-poppins font-bold text-xl">Truth</p>
                <p className="text-xs text-gray-400">See what dashboards won't tell you</p>
                </div>
            </div>
            </div>

            <div className="text-center">
            <a 
                href="https://calendly.com/andrewkidd/truth-session" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-[#86C2CA] hover:bg-white hover:text-[#062B49] text-white font-poppins font-bold px-10 py-5 rounded transition-all shadow-lg text-xl mb-4"
            >
                Book Your Truth Session →
            </a>
            <p className="text-sm text-gray-400">
                Or start with our <Link to="/request-diagnostic" className="underline hover:text-[#86C2CA]">free 5-minute Mini Diagnostic</Link>
            </p>
            </div>
        </div>
        </section>

      {/* Before You Go - The 3am Anxieties */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-poppins font-bold text-4xl md:text-5xl text-[#062B49] text-center mb-6">
            Before you go...
          </h2>
          <p className="text-xl text-center text-[#697A90] mb-12">
            At 3 a.m., you're awake wondering:
          </p>

          <div className="space-y-4 mb-12">
            <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49]">
              <p className="text-lg text-[#697A90]">"What if we're not actually going to deliver this?"</p>
            </div>
            <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49]">
              <p className="text-lg text-[#697A90]">"Why am I the bottleneck again?"</p>
            </div>
            <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49]">
              <p className="text-lg text-[#697A90]">"Are my leadership peers truly aligned—or just saying they are?"</p>
            </div>
            <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49]">
              <p className="text-lg text-[#697A90]">"Am I doing enough, or am I just invisible to everyone I'm holding together?"</p>
            </div>
            <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49]">
              <p className="text-lg text-[#697A90]">"I don't think they see what I'm carrying."</p>
            </div>
          </div>

          <div className="bg-[#062B49] text-white p-10 rounded-lg text-center">
            <p className="text-2xl font-poppins font-bold mb-4">
              These aren't just worries. They're signals.
            </p>
            <p className="text-lg text-gray-300 mb-6">
              Signals that hidden work is compounding—draining energy, clarity, and confidence from your transformation.
            </p>
            <p className="text-xl mb-8">
              That's why we don't offer another framework.<br/>
              <span className="font-poppins font-bold text-[#86C2CA]">We offer operational truth-telling</span> backed by behavioral science and transformation expertise.
            </p>
            <a 
              href="https://calendly.com/andrewkidd/truth-session" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-[#86C2CA] hover:bg-white hover:text-[#062B49] text-white font-poppins font-bold px-10 py-4 rounded transition-all shadow-lg text-lg"
            >
              Book Your Truth Session
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
